<template>
  <Popup :ref="(el) => popup = el"
         @onClose="hide"
         :title="$t('requisites.bulk_edit.title', {count: items.length})"
  >
    <form class="requisites" @submit.prevent>
      <div class="requisites__container">
        <div class="requisites__block">
          <div class="text-muted">{{ $t('requisites.bulk_edit.hint') }}</div>
          <FormInputWrapper v-slot="parentProps" field="ids" form="requisitesBulk">
            <span :class="parentProps.class"></span>
          </FormInputWrapper>
        </div>

        <template v-for="section in sections" :key="section.key">
          <h6 class="mt-3 mb-1 text-capitalize-first">{{ $t(`requisites.bulk_edit.section.${section.key}`) }}</h6>

          <div v-for="field in section.fields" :key="field.name" class="requisites__block align-items-start">
            <div class="requisites__label requisites__label--start text-capitalize-first">
              <label class="d-flex align-items-center gap-2">
                <input type="checkbox"
                       class="form-check-input flex-shrink-0 mt-0"
                       v-model="enabled[field.name]"
                       :disabled="isFieldDisabled(field)">
                <span>{{ $t(field.label) }}</span>
              </label>
            </div>

            <div class="requisites__content">
              <template v-if="field.kind === 'sum' || field.kind === 'count'">
                <FormInputWrapper v-slot="parentProps" :field="`changes[${field.name}]`" form="requisitesBulk">
                  <div class="d-flex align-items-center gap-2">
                    <input v-model="values[field.name]"
                           v-maska
                           :disabled="!enabled[field.name]"
                           :class="parentProps.class"
                           :placeholder="field.nullable ? '' : '0'"
                           data-maska="9 99#"
                           data-maska-tokens="9:[0-9]:repeated"
                           data-maska-reversed
                           type="text" class="form-control">
                    <span v-if="field.kind === 'sum' && currency" class="text-muted">{{ currency }}</span>
                  </div>
                </FormInputWrapper>
                <small v-if="field.kind === 'sum' && mixedCurrency" class="text-danger">{{ $t('requisites.bulk_edit.mixed_currency') }}</small>
                <small v-else-if="enabled[field.name]" class="text-muted">
                  {{ field.nullable ? $t('requisites.bulk_edit.empty_to_clear') : $t('requisites.bulk_edit.empty_is_zero') }}
                </small>
              </template>

              <template v-else-if="field.kind === 'flag'">
                <label class="switch">
                  <input type="checkbox" v-model="values[field.name]" :disabled="!enabled[field.name]">
                  <span class="slider"></span>
                </label>
                <span v-if="field.name === 'isActive'" class="ms-2" :class="{'text-muted': !enabled[field.name]}">
                  {{ values[field.name] ? $t('requisites.bulk_edit.active_on') : $t('requisites.bulk_edit.active_off') }}
                </span>
                <FormInputWrapper v-if="field.name === 'isActive' && needsAgreement" v-slot="parentProps" field="isAgreeBlacklistPaymentMethod" form="requisitesBulk">
                  <div class="mt-2" :class="parentProps.class">
                    <input class="form-check-input" v-model="isAgreeBlacklistPaymentMethod" type="checkbox" id="bulkBlacklistAgreement">
                    <label class="form-check-label" for="bulkBlacklistAgreement">
                      {{ $t('labels.agree_with_risk') }}
                    </label>
                  </div>
                </FormInputWrapper>
              </template>

              <template v-else-if="field.kind === 'switchOff'">
                <span :class="{'text-muted': !enabled[field.name]}">{{ $t('requisites.bulk_edit.white_triangle_off') }}</span>
              </template>

              <template v-else-if="field.kind === 'action'">
                <span :class="{'text-muted': !enabled[field.name]}">{{ $t(field.hint || '') }}</span>
              </template>

              <template v-else-if="field.kind === 'group'">
                <FormInputWrapper v-slot="parentProps" :field="`changes[${field.name}]`" form="requisitesBulk">
                  <Multiselect class="multiselect-sm"
                               :class="parentProps.class"
                               v-model="values[field.name]"
                               :disabled="!enabled[field.name]"
                               :options="groupOptions"
                               :custom-label="groupLabel"
                               :multiple="false"
                               :allow-empty="false"
                               select-label="Select"
                               :placeholder="$t('labels.req_group')"
                  ></Multiselect>
                </FormInputWrapper>
              </template>

              <template v-else-if="field.kind === 'device'">
                <FormInputWrapper v-slot="parentProps" :field="`changes[${field.name}]`" form="requisitesBulk">
                  <Multiselect class="multiselect-sm"
                               :class="parentProps.class"
                               v-model="values[field.name]"
                               :disabled="!enabled[field.name]"
                               :options="deviceOptions"
                               :custom-label="deviceLabel"
                               :multiple="false"
                               :allow-empty="false"
                               select-label="Select"
                               :placeholder="$t('labels.device')"
                  ></Multiselect>
                </FormInputWrapper>
                <small v-if="enabled[field.name]" class="text-warning">{{ $t('requisites.bulk_edit.device_warning') }}</small>
              </template>
            </div>
          </div>
        </template>
      </div>

      <div class="requisites__controls">
        <button class="btn" type="button" @click.prevent="hide">
          <span class="text-capitalize-first">{{ $t('button.cancel') }}</span>
        </button>
        <LoaderButton :on-click="save" class="btn btn--accept">
          <span class="text-capitalize-first">{{ $t('button.save') }}</span>
        </LoaderButton>
      </div>
    </form>
  </Popup>
</template>
<script lang="ts">
import {computed, defineComponent, ref} from 'vue'
import {reactive, toRefs} from '@vue/reactivity';
import {useStore} from "vuex";
import {useNotification} from "@kyvg/vue3-notification";
import Multiselect from "vue-multiselect";
import Popup from "@/apps/public/components/Popup.vue";
import LoaderButton from "@/apps/public/components/LoaderButton.vue";
import FormInputWrapper from "@/components/FormInputWrapper.vue";
import {$t, helper} from "@/service/CommonHelper";
import {api} from "@/service/APIClient";
import TraderRequisitesDTO from "@/models/TraderRequisitesDTO";
import BulkUpdateRequisitesResponseDTO from "@/models/BulkUpdateRequisitesResponseDTO";

type BulkFieldKind = 'sum' | 'count' | 'flag' | 'group' | 'device' | 'switchOff' | 'action'

interface BulkField {
  name: string
  label: string
  kind: BulkFieldKind
  nullable: boolean
  section: string
  visible: boolean
  hint?: string
  initial?: boolean
}

const NO_GROUP = 0

export default defineComponent({
  emits: ['updated'],
  methods: {$t},
  components: {
    Popup,
    LoaderButton,
    FormInputWrapper,
    Multiselect,
  },
  setup(props, {emit}) {
    const store = useStore()
    const notification = useNotification()
    const popup = ref<InstanceType<typeof Popup> | null>(null)
    const state = reactive({
      items: [] as TraderRequisitesDTO[],
      enabled: {} as Record<string, boolean>,
      values: {} as Record<string, any>,
      blacklistMethods: [] as string[],
      isAgreeBlacklistPaymentMethod: false,
    })

    const appData = computed(() => store.getters.getAppData)

    const isReuseEnabled = computed(() => store.getters.getUser?.isRequisitesReuseEnable ?? true)

    const fields = computed<BulkField[]>(() => [
      {name: 'isActive', label: 'requisites.bulk_edit.active', kind: 'flag', nullable: false, section: 'actions', visible: true, initial: true},
      {name: 'resetDayLimits', label: 'requisites.bulk_edit.reset_day_limits', kind: 'action', nullable: false, section: 'actions', visible: true, hint: 'requisites.bulk_edit.reset_day_limits_hint'},
      {name: 'minPayInDealSum', label: 'labels.min_pay_in_deal_sum', kind: 'sum', nullable: false, section: 'pay_in_sums', visible: true},
      {name: 'maxPayInDealSum', label: 'labels.max_pay_in_deal_sum', kind: 'sum', nullable: true, section: 'pay_in_sums', visible: true},
      {name: 'multiplesOfValue', label: 'labels.multiples_of_value', kind: 'count', nullable: true, section: 'pay_in_sums', visible: !!appData.value?.payInMultiplesEnabled},
      {name: 'dayTradesSumLimit', label: 'labels.daily_sum', kind: 'sum', nullable: false, section: 'limits', visible: true},
      {name: 'monthTradesSumLimit', label: 'labels.monthly_sum', kind: 'sum', nullable: false, section: 'limits', visible: true},
      {name: 'dayTradesCountLimit', label: 'labels.daily_qty', kind: 'count', nullable: false, section: 'limits', visible: true},
      {name: 'monthTradesCountLimit', label: 'labels.monthly_qty', kind: 'count', nullable: false, section: 'limits', visible: true},
      {name: 'dayTradesAllCountLimit', label: 'labels.daily_qty_all_deals', kind: 'count', nullable: false, section: 'limits', visible: !!appData.value?.requisitesAllDealsCountLimitsEnabled},
      {name: 'monthTradesAllCountLimit', label: 'labels.monthly_qty_all_deals', kind: 'count', nullable: false, section: 'limits', visible: !!appData.value?.requisitesAllDealsCountLimitsEnabled},
      {name: 'totalTradesSumLimit', label: 'labels.total_sum', kind: 'sum', nullable: false, section: 'limits', visible: !!appData.value?.requisitesTotalLimitEnabled},
      {name: 'totalTradesCountLimit', label: 'labels.total_qty', kind: 'count', nullable: false, section: 'limits', visible: !!appData.value?.requisitesTotalLimitEnabled},
      {name: 'manualResetEnabled', label: 'labels.manual_reset_enabled', kind: 'flag', nullable: false, section: 'limits', visible: !!appData.value?.requisitesTotalLimitEnabled},
      {name: 'maxReuseCount', label: 'labels.maxReuseCount', kind: 'count', nullable: true, section: 'reuse', visible: isReuseEnabled.value},
      {name: 'reuseDaysCount', label: 'labels.reuseDaysCount', kind: 'count', nullable: true, section: 'reuse', visible: isReuseEnabled.value},
      {name: 'reusablePaymentsCountLimit', label: 'labels.reusablePaymentsCountLimit', kind: 'count', nullable: true, section: 'reuse', visible: isReuseEnabled.value},
      {name: 'isFarmsSettingsEnabled', label: 'labels.farmsSettings', kind: 'flag', nullable: false, section: 'farms', visible: true},
      {name: 'concurrentTradesCount', label: 'labels.concurrent_trader_count', kind: 'count', nullable: true, section: 'farms', visible: true},
      {name: 'tradesTimeoutMinutes', label: 'labels.trades_timeout_minutes', kind: 'count', nullable: true, section: 'farms', visible: true},
      {name: 'groupId', label: 'labels.req_group', kind: 'group', nullable: true, section: 'binding', visible: true},
      {name: 'deviceId', label: 'labels.device', kind: 'device', nullable: false, section: 'binding', visible: true},
      {name: 'onlyInternalTransfer', label: 'labels.onlyInternalTransfer', kind: 'flag', nullable: false, section: 'switches', visible: true},
      {name: 'isWhiteTriangle', label: 'labels.white_triangle', kind: 'switchOff', nullable: false, section: 'switches', visible: !!appData.value?.fraudexEnabled},
    ])

    const sections = computed(() => {
      const result: {key: string, fields: BulkField[]}[] = []
      for (const field of fields.value) {
        if (!field.visible) {
          continue
        }
        let section = result.find(item => item.key === field.section)
        if (!section) {
          section = {key: field.section, fields: []}
          result.push(section)
        }
        section.fields.push(field)
      }
      return result
    })

    const mixedCurrency = computed(() => new Set(state.items.map(item => item.currency)).size > 1)
    const currency = computed(() => mixedCurrency.value ? null : (state.items[0]?.currency ?? null))

    const isFieldDisabled = (field: BulkField) => field.kind === 'sum' && mixedCurrency.value

    const needsAgreement = computed(() => {
      return !!state.enabled.isActive
        && state.values.isActive === true
        && state.items.some(item => !item.isActive && state.blacklistMethods.includes(item.method))
    })

    const groupOptions = computed(() => [NO_GROUP, ...store.getters['reqGroups/ids']])
    const deviceOptions = computed(() => store.getters['devices/ids'])

    const groupLabel = (opt: number) => opt === NO_GROUP
      ? helper.trans('requisites.bulk_edit.no_group')
      : store.getters['reqGroups/get'](opt)?.name

    const deviceLabel = (opt: string) => store.getters['devices/get'](opt)?.name

    const initialValue = (field: BulkField) => {
      if (field.initial !== undefined) {
        return field.initial
      }
      if (field.kind === 'flag') {
        return false
      }
      if (field.kind === 'group') {
        return NO_GROUP
      }
      if (field.kind === 'device') {
        return null
      }
      return ''
    }

    const show = (items: TraderRequisitesDTO[], blacklistMethods: string[] = []) => {
      helper.clearErrors('requisitesBulk')
      state.items = items
      state.blacklistMethods = blacklistMethods
      state.isAgreeBlacklistPaymentMethod = false
      state.enabled = {}
      state.values = {}
      for (const field of fields.value) {
        state.enabled[field.name] = false
        state.values[field.name] = initialValue(field)
      }
      popup.value?.show()
    }

    const hide = () => {
      popup.value?.hide()
    }

    const toValue = (field: BulkField) => {
      const raw = state.values[field.name]
      if (field.kind === 'sum' || field.kind === 'count') {
        const text = String(raw ?? '').replace(/\s/g, '')
        if (text === '') {
          return field.nullable ? null : 0
        }
        return Number.parseInt(text)
      }
      if (field.kind === 'flag') {
        return !!raw
      }
      if (field.kind === 'switchOff') {
        return false
      }
      if (field.kind === 'action') {
        return true
      }
      if (field.kind === 'group') {
        return raw === NO_GROUP ? null : raw
      }
      return raw
    }

    const save = async () => {
      const changes: Record<string, any> = {}
      for (const section of sections.value) {
        for (const field of section.fields) {
          if (state.enabled[field.name] && !isFieldDisabled(field)) {
            changes[field.name] = toValue(field)
          }
        }
      }

      if (Object.keys(changes).length === 0) {
        notification.notify({type: 'error', title: helper.trans('requisites.bulk_edit.nothing_selected')})
        return
      }

      helper.clearErrors('requisitesBulk')
      try {
        const response = await api.bulkUpdateRequisites(state.items.map(item => item.id), changes, needsAgreement.value && state.isAgreeBlacklistPaymentMethod)
        emit('updated', response as BulkUpdateRequisitesResponseDTO)
        hide()
      } catch (e: any) {
        if (e.response && e.response.status === 400) {
          helper.handleErrors('requisitesBulk', e)
        } else {
          notification.notify({type: 'error', title: helper.trans('requisites.notice.bulk_update_error')})
        }
      }
    }

    return {
      ...toRefs(state),
      popup,
      sections,
      mixedCurrency,
      needsAgreement,
      currency,
      groupOptions,
      deviceOptions,
      isFieldDisabled,
      groupLabel,
      deviceLabel,
      show,
      hide,
      save,
    }
  },
})
</script>
